/// <amd-module name="TFS/FeatureManagement/Components/FeatureDialog/FeatureDialog" />
import * as React from "react";

import { format as stringFormat } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

import { IButtonProps } from "VSSUI/Button";
import { CornerDialog } from "VSSUI/Dialog";
import { Link } from "VSSUI/Link";

import { ContributedFeature, ContributedFeatureEnabledValue, ContributedFeatureState } from "TFS/FeatureManagement/Contracts/FeatureManagement";
import * as FeatureResources from "TFS/FeatureManagement/Resources";
import { ITfsFeatureService } from "TFS/FeatureManagement/Types";
import { IFeatureAdsService } from "../../FeatureAdsService";
import { IFeaturePanel } from "../../FeaturePanelService";

interface IFeatureDialogComponentProps {
    feature: ContributedFeature;
    message: string;
    showDismissCallout?: boolean;
    title?: string;
    onDismiss: () => void;
}

const telemetryArea = "FeatureDialog";

class FeatureDialogComponent extends VssComponent<IFeatureDialogComponentProps> {
    public static componentType = "featureGlobalDialog";

    private featureService: ITfsFeatureService;
    private reloadOnFeatureSet: boolean;

    constructor(props: IFeatureDialogComponentProps, context: IVssComponentContext) {
        super(props, context);

        this.featureService = context.pageContext.getService<ITfsFeatureService>("ITfsFeatureService");
    }

    public componentDidMount() {
        this.featureService.subscribe(this.onFeatureStateChanged, "featureStateUpdated");
    }

    public render(): JSX.Element {
        const footerButtons: IButtonProps[] = [];

        if (this.props.feature._links && this.props.feature._links.learn) {
            footerButtons.push({
                className: "flex-noshrink",
                text: FeatureResources.LearnMore,
                href: this.props.feature._links.learn.href,
                target: "_blank"
            });
        }

        footerButtons.push({
            primary: true,
            className: "flex-noshrink",
            onClick: this.onTryItClicked,
            text: FeatureResources.FeatureBannerTryItButton
        });

        return (
            <CornerDialog
                onDismiss={this.onDismissedClicked}
                className="feature-dialog"
                footerButtonProps={footerButtons}
                titleProps={{
                    text: this.props.title || this.props.feature.name,
                    className: "feature-name flex-noshrink"
                }}
            >
                <div className="flex-column">
                    <span className="description-text">{this.props.message || this.props.feature.description || ""}</span>
                </div>
            </CornerDialog>
        );
    }

    public componentWillUnmount() {
        this.featureService.unsubscribe(this.onFeatureStateChanged, "featureStateUpdated");

        super.componentWillUnmount();
    }

    private onFeatureStateChanged = (featureState: ContributedFeatureState): void => {
        if (featureState.featureId === this.props.feature.id) {
            this.dismissAd().then(() => {
                const featureAdService = this.context.pageContext.getService<IFeatureAdsService>("IFeatureAdsService");
                featureAdService.prepareShowFeatureEnabledCallout(this.props.feature.id);

                // Reload the page
                if (this.reloadOnFeatureSet) {
                    window.location.reload();
                }
            });
        }
    };

    private onTryItClicked = (): void => {
        this.reloadOnFeatureSet = true;

        const userScoped = this.props.feature.scopes.filter(s => s.userScoped).length > 0;

        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(telemetryArea, "bannerTryItClicked", {
            featureId: this.props.feature.id,
            openedPanel: !userScoped
        });

        if (userScoped) {
            // User-scoped feature. Toggle the scope on for the user right here through an XHR call
            const stateToUpdate = {
                state: ContributedFeatureEnabledValue.Enabled
            } as ContributedFeatureState;

            this.featureService.setFeatureState(this.props.feature, true, undefined, undefined, ContributedFeatureEnabledValue.Enabled);
        } else {
            const featurePanelService = this.context.pageContext.getService<IFeaturePanel>("IFeaturePanel");
            featurePanelService.show(this.props.feature.scopes[0]);
        }
    };

    private onDismissedClicked = (): void => {
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(telemetryArea, "bannerDismissed", {
            featureId: this.props.feature.id
        });

        this.props.onDismiss();
        this.dismissAd();

        if (this.props.showDismissCallout) {
            const featureAdService = this.context.pageContext.getService<IFeatureAdsService>("IFeatureAdsService");
            featureAdService.showFeatureAdDismissedCallout();
        }
    };

    private dismissAd(): Promise<void> {
        const featureAdService = this.context.pageContext.getService<IFeatureAdsService>("IFeatureAdsService");
        return featureAdService.dismissFeatureAd(this.props.feature.id);
    }
}

VssComponent.register(FeatureDialogComponent.componentType, FeatureDialogComponent);
